import {
  AnyAction,
  combineReducers,
  configureStore,
  Dispatch,
  Middleware
} from '@reduxjs/toolkit'
import { setupListeners } from '@reduxjs/toolkit/dist/query'
import { createWrapper } from 'next-redux-wrapper'
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux'
import logger from 'redux-logger'
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE
} from 'redux-persist'
import storage from 'redux-persist/lib/storage'
import { achievementsApi } from './services/achievementsApi'
import { actionBoxesApi } from './services/actionBoxesApi'
import { actionBoxCategoriesApi } from './services/actionBoxCategoriesApi'
import { adminUsersApi } from './services/adminUsersApi'
import { announcementApi } from './services/announcementsApi'
import { rtkQueryErrorLogger } from './services/apiMiddleware'
import { authApi } from './services/authApi'
import { behaviorsApi } from './services/behaviorsApi'
import { campaignCategoriesApi } from './services/campaignCategoriesApi'
import { campaignsApi } from './services/campaignsApi'
import { devicesApi } from './services/devicesApi'
import { featureFlagsApi } from './services/featureFlagsApi'
import { imagesApi } from './services/imagesApi'
import { invoicesApi } from './services/invoicesApi'
import { offerCatalogsApi } from './services/offerCatalogsApi'
import { offerCategoriesApi } from './services/offerCategoriesApi'
import { offersApi } from './services/offersApi'
import { pointsDistributionApi } from './services/pointsDistributionApi'
import { providersApi } from './services/providersApi'
import { targetGroupsApi } from './services/targetGroupsApi'
import { tenantsApi } from './services/tenantsApi'
import { notificationsApi } from './services/notificationsApi'
import { transitValidationApi } from './services/transitValidationApi'
import { translationsApi } from './services/translationsApi'
import { updatesApi } from './services/updatesApi'
import { usersApi } from './services/usersApi'
import { actionBoxReducer } from './slices/actionBoxSlice'
import { actionBoxCategoryReducer } from './slices/actionBoxCategorySlice'
import { adminUserReducer } from './slices/adminUserSlice'
import { announcementReducer } from './slices/announcementSlice'
import { authReducer } from './slices/authSlice'
import { campaignCategoryReducer } from './slices/campaignCategorySlice'
import { campaignReducer } from './slices/campaignSlice'
import { couponReducer } from './slices/couponSlice'
import { errorReducer } from './slices/errorSlice'
import { featureFlagReducer } from './slices/featureFlagSlice'
import { offerCategoryReducer } from './slices/offerCategorySlice'
import { offerReducer } from './slices/offerSlice'
import { pointsDistributionReducer } from './slices/pointsDistributionSlice'
import { providerReducer } from './slices/providerSlice'
import { sidebarReducer } from './slices/sidebarSlice'
import { targetGroupReducer } from './slices/targetGroupSlice'
import { tenantReducer } from './slices/tenantSlice'
import { transitValidationReducer } from './slices/transitValidationSlice'
import { updateCardReducer } from './slices/updateCardSlice'

const persistConfig = {
  key: 'root',
  storage: storage,
  whitelist: ['auth', 'tenant']
}

const rootReducer = combineReducers({
  auth: authReducer,
  error: errorReducer,
  tenant: tenantReducer,
  targetGroup: targetGroupReducer,
  sidebar: sidebarReducer,
  announcement: announcementReducer,
  campaign: campaignReducer,
  offer: offerReducer,
  coupons: couponReducer,
  updateCardDetail: updateCardReducer,
  providerDetail: providerReducer,
  campaignCategory: campaignCategoryReducer,
  adminUser: adminUserReducer,
  offerCategory: offerCategoryReducer,
  featureFlag: featureFlagReducer,
  pointsDistribution: pointsDistributionReducer,
  actionBox: actionBoxReducer,
  actionBoxCategory: actionBoxCategoryReducer,
  transitValidation: transitValidationReducer,
  [usersApi.reducerPath]: usersApi.reducer,
  [announcementApi.reducerPath]: announcementApi.reducer,
  [campaignsApi.reducerPath]: campaignsApi.reducer,
  [campaignCategoriesApi.reducerPath]: campaignCategoriesApi.reducer,
  [offersApi.reducerPath]: offersApi.reducer,
  [offerCategoriesApi.reducerPath]: offerCategoriesApi.reducer,
  [authApi.reducerPath]: authApi.reducer,
  [tenantsApi.reducerPath]: tenantsApi.reducer,
  [featureFlagsApi.reducerPath]: featureFlagsApi.reducer,
  [adminUsersApi.reducerPath]: adminUsersApi.reducer,
  [pointsDistributionApi.reducerPath]: pointsDistributionApi.reducer,
  [invoicesApi.reducerPath]: invoicesApi.reducer,
  [targetGroupsApi.reducerPath]: targetGroupsApi.reducer,
  [updatesApi.reducerPath]: updatesApi.reducer,
  [providersApi.reducerPath]: providersApi.reducer,
  [achievementsApi.reducerPath]: achievementsApi.reducer,
  [behaviorsApi.reducerPath]: behaviorsApi.reducer,
  [imagesApi.reducerPath]: imagesApi.reducer,
  [devicesApi.reducerPath]: devicesApi.reducer,
  [translationsApi.reducerPath]: translationsApi.reducer,
  [offerCatalogsApi.reducerPath]: offerCatalogsApi.reducer,
  [actionBoxesApi.reducerPath]: actionBoxesApi.reducer,
  [actionBoxCategoriesApi.reducerPath]: actionBoxCategoriesApi.reducer,
  [transitValidationApi.reducerPath]: transitValidationApi.reducer,
  [notificationsApi.reducerPath]: notificationsApi.reducer
})

const persistedReducer = persistReducer(persistConfig, rootReducer)
const middleware = (getDefaultMiddleware: any) => {
  const middlewares: Middleware<any, any>[] = []
  let defaultMid = getDefaultMiddleware({
    serializableCheck: {
      ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER]
    }
  })
  if (process.env.NODE_ENV === 'development') {
    middlewares.push(logger as Middleware<Dispatch<AnyAction>>)
  }
  middlewares.push(
    usersApi.middleware,
    authApi.middleware,
    tenantsApi.middleware,
    campaignsApi.middleware,
    campaignCategoriesApi.middleware,
    offersApi.middleware,
    offerCategoriesApi.middleware,
    featureFlagsApi.middleware,
    adminUsersApi.middleware,
    pointsDistributionApi.middleware,
    invoicesApi.middleware,
    targetGroupsApi.middleware,
    announcementApi.middleware,
    updatesApi.middleware,
    providersApi.middleware,
    achievementsApi.middleware,
    behaviorsApi.middleware,
    imagesApi.middleware,
    devicesApi.middleware,
    translationsApi.middleware,
    offerCatalogsApi.middleware,
    actionBoxesApi.middleware,
    actionBoxCategoriesApi.middleware,
    transitValidationApi.middleware,
    notificationsApi.middleware,
    rtkQueryErrorLogger
  )
  return defaultMid.concat(middlewares)
}

const makeStore = () => {
  const isServer = typeof window === 'undefined'
  const store = configureStore({
    reducer: isServer ? rootReducer : persistedReducer,
    middleware
  } as any)
  setupListeners(store.dispatch)
  return store
}

export const store = makeStore()
export const persistor = persistStore(store)

export type AppStore = ReturnType<typeof makeStore>
export type RootState = ReturnType<AppStore['getState']>
export type AppDispatch = AppStore['dispatch']

export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export const wrapper = createWrapper(makeStore, {
  debug: process.env.NODE_ENV !== 'production'
})
